/**
 * ObsImageUploadModal.tsx
 *
 * Self-contained image-upload overlay for People Hub Observation KFAs.
 *
 * Opens when the user taps the camera icon on any checklist row.
 * Matches the HTML UI exactly:
 *   - Title: "Upload Images for {kfaLabel}"
 *   - Two upload slots side-by-side (image drop zone + description textarea)
 *   - Disclaimer note at bottom
 *   - Cancel  /  Proceed buttons
 *
 * Does NOT use Redux. Images are stored locally and returned via onSave().
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  Image,
  ScrollView,
  StyleSheet,
  Platform,
  Alert,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { createApiClient } from '../../../../api/apiClient';

const isWeb = Platform.OS === 'web';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface KfaImage {
  /** Displayed URI (local preview or remote URL) */
  uri: string;
  /** User-editable description; pre-filled with the KFA text */
  description: string;
  /** Original File object (web only) — kept for future API upload */
  file?: File;
  /** Remote URL returned by POST /common/images/upload */
  imageUrl?: string;
  /** Backend file name returned by the upload API */
  fileName?: string;
  /** Same value as `fileName` — this is what gets sent in the activity payload */
  imageBase64?: string;
}

interface SlotState {
  uri: string | null;
  description: string;
  imageUrl?: string;
  fileName?: string;
  imageBase64?: string;
  uploading: boolean;
  error: string | null;
}

interface ObsImageUploadModalProps {
  visible: boolean;
  /** View-only: images can't be replaced, descriptions can't be edited, and Proceed is hidden. */
  readOnly?: boolean;
  /** The KFA question text — used as modal title and default description */
  kfaLabel: string;
  /** Currently saved images for this KFA (up to 2) */
  savedImages: KfaImage[];
  onClose: () => void;
  /** Called when the user presses Proceed — receives up to 2 images */
  onSave: (images: KfaImage[]) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function openFilePicker(): Promise<{ uri: string; file: File } | null> {
  return new Promise((resolve) => {
    const input = (globalThis as any).document?.createElement('input');
    if (!input) { resolve(null); return; }
    input.type  = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
    input.onchange = (e: any) => {
      const file: File = e.target?.files?.[0];
      if (!file) { resolve(null); return; }
      const reader = new FileReader();
      reader.onload = (ev) => resolve({ uri: ev.target?.result as string, file });
      reader.readAsDataURL(file);
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}

// Uploads a single image to blob storage; the returned imageUrl is what gets
// persisted in the activity-completion payload (sent as `imageBase64` by convention).
async function uploadImageToBlob(
  fileForUpload: File | { uri: string; name: string; type: string },
): Promise<{ imageUrl: string; fileName: string } | null> {
  const formData = new FormData();
  formData.append('image', fileForUpload as any);
  const api = createApiClient();
  const res = await api.post('/common/images/upload', formData, {
    headers: { Accept: 'application/json' },
  });
  if (res?.data?.success && res.data.data?.imageUrl) {
    return { imageUrl: res.data.data.imageUrl, fileName: res.data.data.fileName };
  }
  return null;
}

// ─── Single upload slot ───────────────────────────────────────────────────────

interface UploadSlotProps {
  slot: SlotState;
  index: number;
  onPickImage: (index: number) => void;
  onDescriptionChange: (index: number, text: string) => void;
  readOnly?: boolean;
}

const UploadSlot: React.FC<UploadSlotProps> = ({
  slot, index, onPickImage, onDescriptionChange, readOnly,
}) => {
  const hasImage = !!slot.uri;

  return (
    <View style={slotStyles.row}>
      {/* Drop zone / image preview */}
      <Pressable
        style={slotStyles.dropZone}
        onPress={() => !readOnly && !slot.uploading && onPickImage(index)}
        accessibilityRole="button"
        accessibilityLabel={hasImage ? 'Change image' : 'Upload image'}
      >
        {hasImage ? (
          <>
            <Image source={{ uri: slot.uri! }} style={slotStyles.preview} resizeMode="cover" />
            {!readOnly && (
              <View style={slotStyles.previewOverlay}>
                <MaterialIcons name={slot.uploading ? 'hourglass-empty' : 'edit'} size={20} color="#fff" />
                <Text style={slotStyles.previewOverlayText}>
                  {slot.uploading ? 'Uploading…' : 'Tap to change'}
                </Text>
              </View>
            )}
          </>
        ) : (
          <View style={slotStyles.emptyZone}>
            <MaterialIcons name="photo-camera" size={28} color={AdminColors.blue700} />
            <Text style={slotStyles.dropText}>Drag and Drop your file here</Text>
            <Text style={slotStyles.dropSubText}>(only .jpg, .png)</Text>
            <View style={slotStyles.uploadBtn}>
              <MaterialIcons name="file-upload" size={14} color={AdminColors.blue700} />
              <Text style={slotStyles.uploadBtnText}>Upload</Text>
            </View>
          </View>
        )}
      </Pressable>

      {/* Description field */}
      <View style={slotStyles.descCol}>
        <Text style={slotStyles.descLabel}>Description</Text>
        <TextInput
          style={slotStyles.descInput}
          value={slot.description}
          onChangeText={(t) => onDescriptionChange(index, t)}
          editable={!readOnly}
          multiline
          placeholder="Add description…"
          placeholderTextColor={AdminColors.textMuted}
        />
        {!!slot.error && <Text style={slotStyles.errorText}>{slot.error}</Text>}
      </View>
    </View>
  );
};

const slotStyles = StyleSheet.create({
  row: {
    flexDirection: isWeb ? 'row' : 'column',
    gap: 16,
    marginBottom: 16,
  },
  dropZone: {
    width:  isWeb ? 220 : '100%',
    height: 150,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    borderStyle: 'dashed' as any,
    overflow: 'hidden',
    backgroundColor: AdminColors.bgPage,
    flexShrink: 0,
  },
  emptyZone: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: 12,
  },
  dropText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textSecondary,
    textAlign: 'center',
  },
  dropSubText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    textAlign: 'center',
  },
  uploadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderColor: AdminColors.blue700,
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 5,
    marginTop: 4,
    backgroundColor: '#fff',
  },
  uploadBtnText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.blue700,
  },
  preview: {
    width: '100%',
    height: '100%',
  },
  previewOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 6,
  },
  previewOverlayText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: '#fff',
  },
  descCol: {
    flex: 1,
    gap: 6,
  },
  descLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  descInput: {
    flex: 1,
    minHeight: 100,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    backgroundColor: '#fff',
    textAlignVertical: 'top',
  },
  errorText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: '#C62828',
  },
});

// ─── Modal ────────────────────────────────────────────────────────────────────

const ObsImageUploadModal: React.FC<ObsImageUploadModalProps> = ({
  visible,
  readOnly,
  kfaLabel,
  savedImages,
  onClose,
  onSave,
}) => {
  const [slots, setSlots] = useState<[SlotState, SlotState]>([
    { uri: null, description: kfaLabel, uploading: false, error: null },
    { uri: null, description: kfaLabel, uploading: false, error: null },
  ]);

  // Sync saved images into slot state whenever the modal opens or the KFA changes.
  // savedImages is a position-preserving array: savedImages[0] → slot 0,
  // savedImages[1] → slot 1. A null entry means that slot had no image.
  useEffect(() => {
    if (visible) {
      setSlots([
        {
          uri:         savedImages[0]?.uri  ?? null,
          description: savedImages[0]?.description ?? kfaLabel,
          imageUrl:    savedImages[0]?.imageUrl,
          fileName:    savedImages[0]?.fileName,
          imageBase64: savedImages[0]?.imageBase64,
          uploading: false,
          error: null,
        },
        {
          uri:         savedImages[1]?.uri  ?? null,
          description: savedImages[1]?.description ?? kfaLabel,
          imageUrl:    savedImages[1]?.imageUrl,
          fileName:    savedImages[1]?.fileName,
          imageBase64: savedImages[1]?.imageBase64,
          uploading: false,
          error: null,
        },
      ]);
    }
  }, [visible, kfaLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSlot = (index: number, patch: Partial<SlotState>) => {
    setSlots((prev) => {
      const next: [SlotState, SlotState] = [{ ...prev[0] }, { ...prev[1] }];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  // Uploads the picked file and stores the resulting imageUrl/fileName on the slot.
  const uploadAndStore = async (
    index: number,
    fileForUpload: File | { uri: string; name: string; type: string },
  ) => {
    updateSlot(index, { uploading: true, error: null });
    try {
      const result = await uploadImageToBlob(fileForUpload);
      if (result) {
        updateSlot(index, {
          uploading: false,
          imageUrl: result.imageUrl,
          fileName: result.fileName,
          imageBase64: result.imageUrl,
        });
      } else {
        updateSlot(index, { uploading: false, error: 'Unable to upload image' });
      }
    } catch {
      updateSlot(index, { uploading: false, error: 'Unable to upload image' });
    }
  };

  // ── Image picking ──────────────────────────────────────────────────────
  const handlePickImage = async (index: number) => {
    if (isWeb) {
      const result = await openFilePicker();
      if (!result) return;
      updateSlot(index, { uri: result.uri, imageUrl: undefined, fileName: undefined, imageBase64: undefined });
      await uploadAndStore(index, result.file);
    } else {
      // Mobile: Alert sheet → camera or gallery
      const { launchCamera, launchImageLibrary } = await import('react-native-image-picker');
      const handleAsset = async (asset: { uri?: string; fileName?: string; type?: string }) => {
        if (!asset.uri) return;
        updateSlot(index, { uri: asset.uri, imageUrl: undefined, fileName: undefined, imageBase64: undefined });
        await uploadAndStore(index, {
          uri: asset.uri,
          name: asset.fileName || 'image.jpg',
          type: asset.type || 'image/jpeg',
        });
      };
      Alert.alert('Add Image', 'How would you like to add an image?', [
        {
          text: 'Take Photo',
          onPress: async () => {
            const res = await launchCamera({ mediaType: 'photo', quality: 1 });
            if (res.assets?.[0]) await handleAsset(res.assets[0]);
          },
        },
        {
          text: 'Choose from Gallery',
          onPress: async () => {
            const res = await launchImageLibrary({ mediaType: 'photo', quality: 1 });
            if (res.assets?.[0]) await handleAsset(res.assets[0]);
          },
        },
        { text: 'Cancel', style: 'cancel' },
      ]);
    }
  };

  const handleDescriptionChange = (index: number, text: string) => {
    updateSlot(index, { description: text });
  };


  const handleProceed = () => {
    // Preserve slot positions: use a sparse array indexed by slot (0 or 1).
    // Slots with no image are saved as null so that when the modal re-opens,
    // each image is restored to the exact slot the user placed it in.
    const saved: KfaImage[] = slots.map((s) =>
      s.uri !== null
        ? {
            uri: s.uri,
            description: s.description,
            imageUrl: s.imageUrl,
            fileName: s.fileName,
            imageBase64: s.imageBase64,
          }
        : null as unknown as KfaImage,
    );
    // Drop trailing nulls so the count badge only counts real images.
    const trimmed = [...saved];
    while (trimmed.length > 0 && trimmed[trimmed.length - 1] === null) {
      trimmed.pop();
    }
    onSave(trimmed);
    onClose();
  };

  const isUploading = slots.some((s) => s.uploading);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title} numberOfLines={2}>
              Upload Images for {kfaLabel}
            </Text>
            <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
              <MaterialIcons name="close" size={22} color={AdminColors.textPrimary} />
            </TouchableOpacity>
          </View>

          {/* Scrollable body */}
          <ScrollView
            style={styles.body}
            contentContainerStyle={styles.bodyContent}
            showsVerticalScrollIndicator
          >
            {slots.map((slot, i) => (
              <UploadSlot
                key={i}
                slot={slot}
                index={i}
                onPickImage={handlePickImage}
                onDescriptionChange={handleDescriptionChange}
                readOnly={readOnly}
              />
            ))}

            {/* Disclaimer */}
            {!readOnly && (
              <Text style={styles.disclaimer}>
                Images are uploaded as soon as you select them. Add an optional description, then
                press Proceed to attach them to this checklist item.
              </Text>
            )}
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
              <Text style={styles.cancelText}>{readOnly ? 'Close' : 'Cancel'}</Text>
            </TouchableOpacity>
            {!readOnly && (
              <TouchableOpacity
                style={[styles.proceedBtn, isUploading && styles.proceedBtnDisabled]}
                onPress={handleProceed}
                disabled={isUploading}
              >
                <Text style={styles.proceedText}>{isUploading ? 'Uploading…' : 'Proceed'}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 760,
    maxHeight: '90%',
    backgroundColor: '#fff',
    borderRadius: 10,
    borderWidth: isWeb ? 8 : 0,
    borderColor: AdminColors.blue75,
    overflow: 'hidden',
  },

  // ── Header ────────────────────────────────────────────────────────────
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
    gap: 12,
  },
  title: {
    flex: 1,
    fontFamily: FontFamily.bold,
    fontSize: isWeb ? 17 : 15,
    color: AdminColors.textPrimary,
  },
  closeBtn: {
    padding: 4,
    flexShrink: 0,
  },

  // ── Body ──────────────────────────────────────────────────────────────
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: 20,
    gap: 0,
  },

  // ── Disclaimer ────────────────────────────────────────────────────────
  disclaimer: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textSecondary,
    lineHeight: 16,
    marginTop: 4,
    marginBottom: 8,
  },

  // ── Footer buttons ────────────────────────────────────────────────────
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
  },
  cancelBtn: {
    paddingHorizontal: 20,
    paddingVertical: 9,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 20,
    backgroundColor: '#fff',
  },
  cancelText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    color: AdminColors.textPrimary,
  },
  proceedBtn: {
    paddingHorizontal: 20,
    paddingVertical: 9,
    borderRadius: 20,
    backgroundColor: AdminColors.blue800,
  },
  proceedBtnDisabled: {
    opacity: 0.6,
  },
  proceedText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    color: '#fff',
  },
});

export default ObsImageUploadModal;
